import { useCallback, useEffect, useMemo, useState } from 'react';

import { auth } from 'firebase/firebase';
import moment from 'moment';

import { getRange } from '@src/packages/dashboard/utils';

import { useIsMobile } from 'packages/utils';
import useCloudFunction from 'packages/utils/hooks/clouds/useCloudFunction';

import { DATE_FROM_DEFAULT } from '../../../constants';
import { PAGE_SIZE_BY_DEFAULT, PAGE_SIZE_BY_DEFAULT_MOBILE } from '../../TableCard/constants';

// const PAGE_SIZE_DEFAULT = 10;
// const PAGE_SIZE_MOBILE = 5;

/**
 * Generic hook for table data fetching and transformation
 * @param {Object} options
 * @param {Object} options.queryParams - Query parameters from widget
 * @param {Object} options.filters - Dashboard filters
 * @param {number} options.refresh - Refresh trigger
 * @param {string} options.orgId - Organization ID
 * @param {Array} options.queryDivisionIds - Role-resolved division ids for the query
 * @param {Object} options.boardSelected - Selected board
 * @param {Object} options.modalHandlers - Modal handlers
 * @param {Object} options.widget - Widget configuration
 * @param {string} options.op - API operation to call
 * @param {Function} options.buildParams - Function to build cloud function params
 * @param {Function} options.buildColumns - Function to build table columns from data
 * @param {Function} options.buildDataSource - Function to build table dataSource from data
 * @param {Function} options.getTotalEvents - Function to extract total events from data
 * @param {Object} options.additionalDependencies - Additional dependencies for useCloudFunction
 * @param {Object} [options.columnFilters] - Column search filters for server-side search
 * @param {Object} [options.columnSearchHandlers] - { onColumnSearch, onTableChange } for tableRenderer
 * @param {React.MutableRefObject} [options.columnSearchActionsRef] - Ref to receive { resetPage, refetch } for onColumnSearch orchestration
 */
export const useGenericTableData = ({
  queryParams,
  filters,
  refresh,
  orgId,
  queryDivisionIds,
  boardSelected,
  widget,
  op,
  buildParams,
  buildColumns,
  buildDataSource,
  getTotalEvents = data => data?.hits?.total?.value || data?.total || 0,
  additionalDependencies = [],
  enrichData,
  columnFilters = {},
  columnSearchHandlers,
  columnSearchActionsRef,
}) => {
  const { isMobile } = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPosition, setLastPosition] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_BY_DEFAULT);
  const [enrichedData, setEnrichedData] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);

  const {
    dateRange,
    relative: { dateType, relativeAmount, isRelative },
    groupBy,
  } = filters;

  const dateRangeFormated = useMemo(() => {
    if (dateRange?.length === 2) {
      return [moment(dateRange[0]), moment(dateRange[1])];
    }
    return dateRange || [DATE_FROM_DEFAULT, moment()];
  }, [dateRange]);

  /* `useCloudFunction` treats an empty dependency as "not ready yet" and skips the request.
   * `queryDivisionIds` is `[]` when an org-wide role has every division selected, so it must
   * reach the dependency list as a key that still changes with the selection. */
  const queryDivisionIdsKey = useMemo(
    () => (queryDivisionIds?.length ? [...queryDivisionIds].sort().join(',') : 'all'),
    [queryDivisionIds],
  );

  const commonParams = useMemo(
    () => ({
      uid: auth.currentUser.uid,
      organizationId: orgId,
      range: getRange(dateRangeFormated, dateType, isRelative, relativeAmount),
      interval: groupBy,
      divisionIds: boardSelected?.divId ? [boardSelected.divId] : queryDivisionIds,
      lastPosition,
      pageSize: isMobile ? PAGE_SIZE_BY_DEFAULT_MOBILE : pageSize,
    }),
    [
      orgId,
      dateRangeFormated,
      dateType,
      isRelative,
      relativeAmount,
      groupBy,
      boardSelected?.divId,
      queryDivisionIds,
      lastPosition,
      isMobile,
      pageSize,
    ],
  );

  const dashboardParams = useMemo(() => {
    const paramsWithFilters = {
      ...commonParams,
      ...(Object.keys(columnFilters || {}).length > 0 && { columnFilters }),
    };
    if (!buildParams) return paramsWithFilters;
    return buildParams(queryParams, paramsWithFilters);
  }, [buildParams, queryParams, commonParams, columnFilters]);

  const { data, loading, mutateAsync } = useCloudFunction({
    params: dashboardParams,
    dependencies: [
      refresh,
      dateRange,
      groupBy,
      queryDivisionIdsKey,
      filters.relative,
      filters.dateRange,
      filters.groupBy,
      currentPage,
      pageSize,
      JSON.stringify(columnFilters || {}),
      ...additionalDependencies,
    ],
    op,
  });

  const effectiveData = useMemo(() => {
    if (!enrichData) return data;
    return enrichedData;
  }, [data, enrichData, enrichedData]);

  const totalEvents = useMemo(() => getTotalEvents(effectiveData), [effectiveData, getTotalEvents]);

  const dataBuilded = useMemo(() => {
    if (!buildDataSource || !effectiveData) return [];
    return buildDataSource(effectiveData, { queryParams, widget, boardSelected });
  }, [effectiveData, buildDataSource, queryParams, widget, boardSelected]);

  const cols = useMemo(() => {
    if (!buildColumns || !effectiveData) return [];
    return buildColumns(effectiveData, { queryParams, widget, boardSelected, orgId });
  }, [buildColumns, effectiveData, queryParams, widget, boardSelected, orgId]);

  const handlePageChange = useCallback((page, pSize) => {
    setCurrentPage(page);
    setPageSize(pSize);
    setLastPosition((page - 1) * pSize);
  }, []);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
    setLastPosition(0);
  }, []);

  const refetch = useCallback(() => {
    mutateAsync(dashboardParams);
  }, [mutateAsync, dashboardParams]);

  if (columnSearchActionsRef) {
    columnSearchActionsRef.current = { resetPage, refetch };
  }

  useEffect(() => {
    if (!data || !enrichData) {
      setEnrichedData(null);
      return;
    }

    let cancelled = false;
    const runEnrich = async () => {
      setEnrichLoading(true);
      try {
        const result = await enrichData(data);
        if (!cancelled) setEnrichedData(result);
      } finally {
        if (!cancelled) setEnrichLoading(false);
      }
    };
    runEnrich();

    return () => {
      cancelled = true;
    };
  }, [data, enrichData]);

  const returnData = useMemo(() => {
    const base = {
      metrics: dataBuilded,
      columns: cols,
      totalEvents,
      currentPage,
      pageSize,
      handlePageChange,
    };
    if (columnSearchHandlers) {
      return {
        ...base,
        columnFilters: columnFilters || {},
        onColumnSearch: columnSearchHandlers.onColumnSearch,
        onTableChange: columnSearchHandlers.onTableChange,
      };
    }
    return base;
  }, [
    dataBuilded,
    cols,
    totalEvents,
    currentPage,
    pageSize,
    handlePageChange,
    columnFilters,
    columnSearchHandlers,
  ]);

  return {
    data: returnData,
    loading: loading || enrichLoading,
  };
};
